import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Notification, NotificationSettings, NotificationType } from '../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications$ = new BehaviorSubject<Notification[]>([]);
  private settings$ = new BehaviorSubject<NotificationSettings>({
    atMentions: { notification: true, email: true },
    assigneeChanges: { notification: true, email: true },
    statusChanges: { notification: true, email: true }
  });

  constructor() {
    this.loadMockData();
  }

  getNotifications(): Observable<Notification[]> {
    return this.notifications$.asObservable();
  }

  getUnreadCount(): Observable<number> {
    return this.notifications$.pipe(
      map(notifications => notifications.filter(n => !n.isRead).length)
    );
  }

  markAllAsRead(): void {
    const updated = this.notifications$.value.map(n => ({ ...n, isRead: true }));
    this.notifications$.next(updated);
  }

  markAsRead(id: string): void {
    const updated = this.notifications$.value.map(n => 
      n.id === id ? { ...n, isRead: true } : n
    );
    this.notifications$.next(updated);
  }

  getSettings(): Observable<NotificationSettings> {
    return this.settings$.asObservable();
  }

  updateSettings(settings: NotificationSettings): void {
    this.settings$.next(settings);
  }

  private loadMockData(): void {
    const mockNotifications: Notification[] = [
      {
        id: '1',
        type: NotificationType.MENTION,
        action: 'mentioned you in a note',
        userName: 'John Smith',
        entityTitle: 'Speeding violation on Highway 101',
        driverId: 'JONMIH',
        terminal: 'Aberdeen',
        userFriendlyId: 'C-1194',
        status: 'In Progress',
        isRead: false,
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
        entityType: 'intervention',
        interventionId: '11F3B25E-E44B-4125-B461-D1D766ADC6AA',
        driverGuid: 'CD3CA7F3-F9E9-4CD6-9962-12B997F7B195',
        assignee: '5ef532ea-449b-48dc-b40c-f177f87fedc8'
      },
      {
        id: '2',
        type: NotificationType.ASSIGNED,
        action: 'assigned an intervention to you',
        userName: 'Sarah Johnson',
        entityTitle: 'Hard braking incident',
        driverId: 'JONMIH',
        terminal: 'Portland',
        userFriendlyId: 'C-1195',
        status: 'Open',
        isRead: false,
        createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000),
        entityType: 'intervention',
        interventionId: '7ED06CA8-95D7-4959-84F2-860289BE0CC5',
        driverGuid: 'CD3CA7F3-F9E9-4CD6-9962-12B997F7B195',
        assignee: '5ef532ea-449b-48dc-b40c-f177f87fedc8'
      },
      {
        id: '3',
        type: NotificationType.STATUS_CHANGE,
        action: 'changed status to Resolved',
        userName: 'Mike Davis',
        entityTitle: 'Seatbelt compliance issue',
        driverId: 'SHIT',
        terminal: 'Seattle',
        userFriendlyId: 'C-1196',
        status: 'Resolved',
        isRead: false,
        createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000),
        entityType: 'intervention',
        interventionId: '0F871887-4183-4149-88EE-AA0924805E99',
        driverGuid: '93910231-AE1D-41FB-BA85-0294B89A0801',
        assignee: 'Unassigned'
      },
      {
        id: '4',
        type: NotificationType.MENTION,
        action: 'mentioned you in a comment',
        userName: 'Lisa Chen',
        entityTitle: 'Distracted driving incident',
        driverId: 'JONMIH',
        terminal: 'Aberdeen',
        userFriendlyId: 'C-1197',
        status: 'In Progress',
        isRead: false,
        createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000),
        entityType: 'intervention',
        interventionId: 'C5445007-91A8-4DB8-A9F3-E7A6402425FB',
        driverGuid: 'CD3CA7F3-F9E9-4CD6-9962-12B997F7B195',
        assignee: '5b6fcbd9-64f2-4323-8f5b-6880dfa4efd2'
      },
      {
        id: '5',
        type: NotificationType.ASSIGNED,
        action: 'assigned an intervention to you',
        userName: 'Robert Wilson',
        entityTitle: 'Following too closely',
        driverId: 'SCHRN',
        terminal: 'Tacoma',
        userFriendlyId: 'C-1198',
        status: 'Open',
        isRead: false,
        createdAt: new Date(Date.now() - 10 * 60 * 60 * 1000),
        entityType: 'intervention',
        interventionId: 'A9D3BA29-A482-4ED3-B25F-AD87C9AB0372',
        driverGuid: '7141EC7A-B319-48A3-BB66-4AFB3E3892CF',
        assignee: 'Unassigned'
      }
    ];
    this.notifications$.next(mockNotifications);
  }
}